/** @format */

import {Alert, Box, LinearProgress} from '@mui/material';
import {Trans} from 'react-i18next';

import {useNavigate} from 'react-router-dom';

import {format} from 'date-fns';

import {useFlags} from 'launchdarkly-react-client-sdk';

import Breadcrumbs from 'components/Breadcrumbs';

import {useCompanyContext} from 'contexts/CompanyContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import {groupChecksByName, groupedChecks} from 'helpers/checkHelpers';
import {connectedTokens} from 'helpers/connectHelpers';
import {useCallbackOnReturn, useDebugDate} from 'helpers/routerHelpers';

import {ROUTES} from 'constants/ROUTES';

import {getStatusData} from 'pages/HomeGrouping/helpers/getStatusData';

import {ProjectStatus} from 'pages/HomeGrouping/components/ProjectGroup/components/ProjectStatus';

import {getTokenStatus} from 'pages/HomeGrouping/helpers/getTokenStatus';

import {isAdTypeConnected} from 'helpers/adTypeHelpers';

import {useGetCurrentUserQuery} from './graphql/__generated__';

import AiModule from './components/AiModule';
import BudgetAndExpese from './components/BudgetAndExpese';
import {Campaigns} from './components/Campaigns';
import IssuesNew from './components/Issues';
import KpiAndIndicators from './components/KpiAndIndicators';
import NewProjectState from './components/NewProjectState';
import {ProjectType} from './components/ProjectType';
import {Recommendation} from './components/Recommendation';
import {
  BreadcrumbsWrapper,
  Header,
  ProjectButton,
  ProjectButtonsWrapper,
  StyledAutoAwesomeIcon,
  StyledBarChartIcon,
  StyledEditIcon,
  StyledHistoryIcon,
  Title,
  TitleWrapper,
} from './styles';

const Project = () => {
  const now = useDebugDate() ?? new Date();
  const navigate = useNavigate();

  const {isRecommendationsFeatureEnabled, isAiAgentChatEnabled} = useFlags();

  const {currentAdType, currentCompany} = useCompanyContext();
  const {mixpanel} = useMixpanelContext();
  const {loading, error, data, refetch} = useGetCurrentUserQuery({
    variables: {
      date: format(now, 'yyyy-MM-dd'),
    },
  });

  useCallbackOnReturn(refetch);

  const {
    sensorExclusions = [],
    tokens = [],
    regions = [],
    lastChecks = [],
    sensorGroups = {red: [], yellow: [], gray: []},
    id: companyId,
    mediaplanType,
  } = data?.currentUser?.currentCompany || {};

  const breadcrumbs = [
    {
      href: ROUTES.INDEX,
      title: <Trans>Проекты</Trans>,
    },
    {
      href: `/${ROUTES.HOME}`,
      title: data?.currentUser?.currentCompany?.name,
    },
  ];

  const connectedTokensResult = connectedTokens(tokens);

  const isAdTypeConnectedResult = isAdTypeConnected(connectedTokensResult, currentAdType);

  const tokenStatus = data?.currentUser?.currentCompany
    ? getTokenStatus(data.currentUser.currentCompany.stats, tokens[0])
    : null;

  const statusData = getStatusData(tokenStatus, currentCompany.name);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const handleProjectEdit = () => {
    navigate(`/home/project/edit?companyId=${currentCompany.id}`);
  };

  const handleShowYandexHistory = () => {
    mixpanel.track('click_project_changelog_button', {project_uuid: currentCompany.uuid});
    navigate(`/${ROUTES.HOME}/${ROUTES.YA_DIRECT_CHANGE_LOG}`);
  };

  const handleShowCampaignStatistics = () => {
    navigate(`/${ROUTES.HOME}/${ROUTES.CAMPAIGN_STATISTICS}?companyId=${currentCompany.id}`);
  };

  const handleOpenChat = () => {
    navigate(`/${ROUTES.HOME}/${currentCompany.id}/chats`);
  };

  const isShownStatusChip = Boolean(
    statusData && tokenStatus !== 'READY' && tokenStatus !== 'NOT_READY',
  );

  const providers = tokens.map(t => t.provider);
  const isShownAiModule = currentAdType === 'CONTEXT' && providers.includes('YANDEX');

  const groupedChecksResult = groupedChecks({
    allChecks: lastChecks,
    sensorExclusions,
    regions,
  });

  const {validChecks, excludedChecks, needRegionsChecks} = groupedChecksResult;

  const groupedLastChecks = groupChecksByName(validChecks);

  if (tokenStatus === 'NEW') {
    return (
      <>
        <NewProjectState
          companyName={currentCompany.name}
          breadcrumbs={breadcrumbs}
          companyId={currentCompany.id}
          mediaplanType={mediaplanType}
          tokens={tokens}
        />
        <IssuesNew
          validChecks={groupedLastChecks}
          excludedChecks={excludedChecks}
          needRegionsChecks={needRegionsChecks}
          sensorGroups={sensorGroups}
          companyId={companyId || ''}
        />
      </>
    );
  }

  return (
    <Box>
      <BreadcrumbsWrapper>
        <Breadcrumbs items={breadcrumbs} />
      </BreadcrumbsWrapper>

      <Header>
        <TitleWrapper>
          <Title>{currentCompany.name}</Title>
          {mediaplanType && <ProjectType mediaplanType={mediaplanType} tokens={tokens} />}
        </TitleWrapper>
        <ProjectButtonsWrapper isShownStatusChip={isShownStatusChip}>
          {isShownStatusChip && statusData && <ProjectStatus data={statusData} />}
          <ProjectButton onClick={handleShowYandexHistory}>
            <StyledHistoryIcon />
            Яндекс Директ{'\n'}История изменений
          </ProjectButton>
          <ProjectButton onClick={handleShowCampaignStatistics}>
            <StyledBarChartIcon />
            Статистика{'\n'}по кампаниям
          </ProjectButton>
          {isAiAgentChatEnabled && (
            <ProjectButton onClick={handleOpenChat}>
              <StyledAutoAwesomeIcon />
              <Trans>Эй, Джарвис</Trans>
            </ProjectButton>
          )}
          <ProjectButton variant='contained' onClick={handleProjectEdit}>
            <StyledEditIcon />
            Настройка{'\n'}проекта
          </ProjectButton>
        </ProjectButtonsWrapper>
      </Header>

      <BudgetAndExpese tokenStatus={tokenStatus} currentCompany={currentCompany} />

      <KpiAndIndicators tokenStatus={tokenStatus} />

      {isShownAiModule && <AiModule platforms={providers} />}

      {isRecommendationsFeatureEnabled && <Recommendation />}

      {isAdTypeConnectedResult && groupedLastChecks.length > 0 && (
        <IssuesNew
          validChecks={groupedLastChecks}
          excludedChecks={excludedChecks}
          needRegionsChecks={needRegionsChecks}
          sensorGroups={sensorGroups}
          companyId={companyId || ''}
        />
      )}

      <Campaigns
        lastChecks={validChecks}
        sensorGroups={sensorGroups}
        mediaplanType={mediaplanType}
      />
    </Box>
  );
};

export default Project;
